import React from 'react';
import { AppState, BookingImage, Quote } from 'acscp/store';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { QuoteFlightDetails } from 'acscp/components/quotes/quoteFlightDetails';
import style from './quote.module.scss';
import { CroppedImage } from 'acscp/components/common/croppedImage';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import classNames from 'classnames';
import { FormatCurrency } from 'acscp/components/common/helpers/formatCurrency';
import { FlightPlan } from 'acscp/components/legs/flightPlan/flightPlan';
import { SingleLineFlightPlanTrack } from 'acscp/components/legs/flightPlan/singleLineFlightPlanTrack';
import { SplitLineFlightPlanTrack } from 'acscp/components/legs/flightPlan/splitLineFlightPlanTrack';
import { Background, Row } from 'acscp/components/common/row';
import { QuoteImageGallery } from 'acscp/components/quotes/quoteImageGallery';
import { QuoteImageGalleryFullscreen } from 'acscp/components/quotes/quoteImageGalleryFullscreen';
import { QuoteButtons } from 'acscp/components/quotes/quoteButtons';
import { MediaQuery } from 'acscp/components/common/mediaQuery';
import { userIsLoggedIn } from 'acscp/store/helpers/authHelper';
import { connect } from 'react-redux';
import { CharterPrice, QuoteDetails } from 'acscp/components/quotes/quoteDetails';
import { Department } from 'acscp/api/models/customTypes/department';
import { QuoteCargoTypeInformation } from 'acscp/components/quotes/quoteCargoTypeInformation';
import { QuoteType } from 'acscp/api/models/customTypes/quoteType';
import { QuoteDetailsHeader } from 'acscp/components/quotes/quoteDetailsHeader';
import { isQuoteInAcmiWorkflow } from 'acscp/store/helpers/quoteHelper';

interface QuoteElementOwnProps {
    quote: Quote;
    collapsible: boolean;
    jetCardHolder: boolean;
    jetCardType: string | null;
    disabled?: boolean;
}

interface QuoteElementStateProps {
    userIsLoggedIn: boolean;
}

type QuoteElementProps = QuoteElementOwnProps & QuoteElementStateProps;

interface QuoteElementState {
    collapsed: boolean;
    fullscreenImages: BookingImage[];
    tabIndex: number;
    isViewportNarrow: boolean;
}

const messages = defineMessages({
    quoteSectionTitle: {
        id: 'components.quotes.quote.sectionTitle',
        defaultMessage: 'QUOTE {quoteNumber}'
    },
    optionSectionTitle: {
        id: 'component.options.quote.optionSectionTitle',
        defaultMessage: 'OPTION {quoteNumber}'
    },
    offerSectionTitle: {
        id: 'component.options.quote.offerSectionTitle',
        defaultMessage: 'OFFER {quoteNumber}'
    }
});

const getImageGalleryTabIndex = (department: Department, quoteType: QuoteType): number => {
    switch (department) {
        case Department.Cargo:
            return 2;
        case Department.COMMJ:
        case Department.ACMI:
            return quoteType === QuoteType.Series ? 2 : 1;
        case Department.PJETS:
            return 1;
    }
};

export class QuoteElementPresentational
    extends React.Component<QuoteElementProps & InjectedIntlProps, QuoteElementState> {

    constructor(props: QuoteElementProps & InjectedIntlProps) {
        super(props);
        this.state = {
            collapsed: true,
            fullscreenImages: [],
            tabIndex: 0,
            isViewportNarrow: false,
        };
    }

    handleViewportChange = (queryMatches: boolean) => {
        this.setState(prevState => ({
            isViewportNarrow: queryMatches,
            // If we moved into the mobile breakpoint, while being on one of the image tabs,
            // then move to one of the valid tabs (the details tab here)
            tabIndex: queryMatches
                && prevState.tabIndex > 0
                && prevState.fullscreenImages.length === 0 ? 0 : prevState.tabIndex
        }));
    }

    toggleCollapse = () => {
        if (this.props.collapsible) {
            this.setState({
                collapsed: !this.state.collapsed
            });
        }
    }

    onTabSwitch = (tabIndex: number) => {
        this.setState({
            tabIndex,
            // We interacted with tabs, so the quote element should not be collapsed.
            collapsed: false
        });
    }

    selectImageGalleryTab = (department: Department, quoteType: QuoteType) => {
        const imageGalleryIndex = getImageGalleryTabIndex(department, quoteType);
        if (this.state.isViewportNarrow) {
            this.showImageGalleryFullscreen();
        } else {
            this.setState({ tabIndex: imageGalleryIndex });
        }
    }
    selectFloorplanTab = () => {
        const floorplanIndex = this.props.quote.quoteType === QuoteType.Series ? 3 : 2;
        if (this.state.isViewportNarrow) {
            this.showFloorplanFullscreen();
        } else {
            this.setState({ tabIndex: floorplanIndex });
        }
    }

    showImageGalleryFullscreen = () =>
        this.setState({ fullscreenImages: this.props.quote.images.all })

    showFloorplanFullscreen = () => {
        if (this.props.quote.images.floorplan) {
            this.setState({ fullscreenImages: [this.props.quote.images.floorplan] });
        }
    }

    hideFullscreen = () => this.setState(prevState => ({
        fullscreenImages: [],
        tabIndex: prevState.isViewportNarrow ? 0 : prevState.tabIndex,
        // We interacted with image gallery, so the quote element should not be collapsed.
        collapsed: false
    }))

    render(): JSX.Element {
        const { quote, collapsible, intl, jetCardHolder, jetCardType, disabled } = this.props;
        const { collapsed, fullscreenImages, tabIndex } = this.state;
        const { department: departmentKey, quoteType } = quote;

        const getQuoteName = (): string => {
            // If quoteIdentifier was deleted, quoteTitle is an empty string.
            // If quoteTitle is null then want a fallback.
            if (quote.quoteTitle !== null) {
                return quote.quoteTitle;
            }

            switch (departmentKey) {
                case Department.ACMI:
                    if (isQuoteInAcmiWorkflow(quote)) {
                        return intl.formatMessage(messages.offerSectionTitle, {quoteNumber: quote.order});
                    } else {
                        return intl.formatMessage(messages.optionSectionTitle, {quoteNumber: quote.order});
                    }
                case Department.COMMJ:
                    return intl.formatMessage(messages.optionSectionTitle, {quoteNumber: quote.order});
                case Department.Cargo:
                case Department.PJETS:
                    return intl.formatMessage(messages.quoteSectionTitle, {quoteNumber: quote.order});
            }
        };

        const getImageGalleryImageTitleText = (quoteName: string): string => {
            if (quoteName === '') {
                return quote.aircraftType
                    ? quote.aircraftType
                    : '';
            }
            return quote.aircraftType
                ? `${quoteName} - ${quote.aircraftType}`
                : quoteName;
        };

        const quoteNameWithFallback = getQuoteName();
        const imageGalleryTitleText = getImageGalleryImageTitleText(quoteNameWithFallback);
        const tabStyle = departmentKey === Department.Cargo
            ? style.TabCargo
            : quoteType === QuoteType.Series
                ? quote.images.floorplan
                    ? style.TabSeries
                    : style.TabSeriesNoFloorplan
                : quote.images.floorplan
                    ? style.TabStandard
                    : style.TabStandardNoFloorplan;

        // Note: floorplan images can always be accessed from the main image gallery tab anyway
        const shouldShowFloorplanTab = departmentKey !== Department.Cargo && quote.images.floorplan;
        const showAcceptButton = ENABLE_CREATE_ACCOUNT || (this.props.userIsLoggedIn && departmentKey === Department.PJETS);
        const showDisclaimerInImageTab = departmentKey === Department.Cargo && quote.shouldShowPictureTypicalExampleText;

        return (
            <div
                className={classNames(style.Quote, {
                    [style.Collapsed]: collapsed && collapsible,
                    [style.Disabled]: disabled
                })}
            >
                <MediaQuery query={'(max-width: 576px)'} onChange={this.handleViewportChange} />
                <Row className={style.HeaderBar} background={Background.Grey}>
                    {<div
                        className={classNames(style.QuoteTitle, {[style.NoTitleQuote] : !quoteNameWithFallback})}
                        onClick={this.toggleCollapse}
                    >
                        {collapsible &&
                            <div className={classNames(style.Expander, {[style.Collapsed]: collapsed})}>
                                <ArrowIcon />
                            </div>
                        }

                        {quoteNameWithFallback && <div className={style.QuoteTitleText}>
                            {`${quoteNameWithFallback}${quote.charterType ? (' - ' + quote.charterType) : ''}`}
                        </div>}
                    </div>}
                </Row>
                <div
                    className={style.CollapsedBody}
                    onClick={this.toggleCollapse}
                >
                    {quote.images.exterior
                        ? <CroppedImage ratio={16 / 9} src={quote.images.exterior.url} />
                        : null}
                    <div className={classNames(style.Summary, {[style.SummaryMissingImage]: !quote.images.exterior})}>
                        <div className={style.TextContent}>
                            <div className={style.Aircraft}>
                                {quote.aircraftType}
                            </div>
                            <div className={style.Price}>
                                <FormatCurrency currency={quote.sellCurrency} price={quote.sellPrice} />
                            </div>
                        </div>
                        <button
                            className={departmentKey === Department.Cargo
                                ? style.CargoButton
                                : style.Button}
                            onClick={this.toggleCollapse}
                        >
                            <FormattedMessage id={'components.quotes.quote.view'} defaultMessage={'View'} />
                        </button>
                    </div>
                </div>
                <Row className={style.ExpandedBody}>
                    <div className={style.QuoteContainer}>
                        {departmentKey === Department.PJETS &&
                            <div className={style.FlightPlanContainer}>
                                <FlightPlan
                                    desktopFlightPlan={
                                        <SingleLineFlightPlanTrack
                                            legs={quote.legs}
                                            withDurations={true}
                                            withBackground={false}
                                        />}
                                    mobileFlightPlan={<SplitLineFlightPlanTrack legs={quote.legs}/>}
                                />
                            </div>}
                        <Tabs
                            className={style.TabsContainer}
                            selectedTabClassName={departmentKey === Department.Cargo
                                ? style.SelectedCargo
                                : style.Selected}
                            selectedIndex={tabIndex}
                            onSelect={this.onTabSwitch}
                        >
                            <TabList className={style.Tabs}>
                                <Tab
                                    className={tabStyle}
                                >
                                    <QuoteDetailsHeader departmentKey={departmentKey} />
                                </Tab>
                                {quoteType === QuoteType.Series &&
                                    <Tab className={tabStyle}>
                                        <FormattedMessage
                                            id={'components.quotes.quote.aircraftDetails'}
                                            defaultMessage={'Aircraft Details'}
                                        />
                                    </Tab>
                                }
                                {quote.cargoTypeInformation.length > 0 &&
                                    <Tab className={tabStyle}>
                                        <FormattedMessage
                                            id={'components.quotes.quote.cargoTypeInformation'}
                                            defaultMessage={'Terms and Conditions'}
                                        />
                                    </Tab>
                                }
                                {quote.images && quote.images.all.length > 0 &&
                                    <>
                                        <Tab className={classNames(tabStyle, style.ImageGalleryTab)}>
                                            <FormattedMessage
                                                id={'components.quotes.quote.imageGallery'}
                                                defaultMessage={'Image Gallery'}
                                            />
                                        </Tab>
                                        {shouldShowFloorplanTab &&
                                            <Tab className={classNames(tabStyle, style.ImageGalleryTab)}>
                                                <FormattedMessage
                                                    id={'components.quotes.quote.floorplan'}
                                                    defaultMessage={'Floorplan'}
                                                />
                                            </Tab>
                                        }
                                    </>
                                }
                            </TabList>
                            <TabPanel>
                                <QuoteFlightDetails
                                    quote={quote}
                                    userIsLoggedIn={this.props.userIsLoggedIn}
                                    onShowImageGallery={() => this.selectImageGalleryTab(departmentKey, quoteType)}
                                    onShowFloorplan={this.selectFloorplanTab}
                                    jetCardHolder={jetCardHolder}
                                    jetCardType={jetCardType}
                                    disabled={disabled}
                                />
                            </TabPanel>
                            {quoteType === QuoteType.Series &&
                                <TabPanel>
                                    <QuoteDetails
                                        quote={quote}
                                        userIsLoggedIn={this.props.userIsLoggedIn}
                                        onShowImageGallery={() => this.selectImageGalleryTab(departmentKey, quoteType)}
                                        onShowFloorplan={this.selectFloorplanTab}
                                        jetCardHolder={jetCardHolder}
                                        jetCardType={jetCardType}
                                        disabled={disabled}
                                    />
                                </TabPanel>
                            }
                            {quote.cargoTypeInformation.length > 0 &&
                                <TabPanel>
                                    <QuoteCargoTypeInformation
                                        cargoTypeInformation={quote.cargoTypeInformation}
                                    />
                                </TabPanel>
                            }
                            {quote.images && quote.images.all.length > 0 &&
                                <>
                                    <TabPanel>
                                        <div className={style.QuoteImageGallery}>
                                            <div className={style.QuoteImageGalleryInner}>
                                                <QuoteImageGallery
                                                    title={imageGalleryTitleText}
                                                    showCloseButton={false}
                                                    onFullscreen={this.showImageGalleryFullscreen}
                                                    bookingImages={quote.images.all}
                                                    showDisclaimer={showDisclaimerInImageTab}
                                                />
                                            </div>
                                        </div>
                                    </TabPanel>
                                    {shouldShowFloorplanTab &&
                                        <TabPanel>
                                            <div className={style.QuoteImageGallery}>
                                                <div className={style.QuoteImageGalleryInner}>
                                                    <QuoteImageGallery
                                                        title={imageGalleryTitleText}
                                                        showCloseButton={false}
                                                        onFullscreen={this.showFloorplanFullscreen}
                                                        bookingImages={[quote.images.floorplan!]}
                                                    />
                                                </div>
                                            </div>
                                        </TabPanel>
                                    }
                                </>
                            }
                        </Tabs>
                        {quote.sellPrice !== null &&
                            <CharterPrice
                                quote={quote}
                                jetCardHolder={jetCardHolder}
                                className={classNames(
                                    style.CharterPrice,
                                    {[style.CharterPriceCargo]: quote.department === Department.Cargo}
                                )}
                                labelClassName={classNames(
                                    style.CharterPriceLabel,
                                    {[style.CharterPriceLabelCargo]: quote.department === Department.Cargo}
                                )}
                                currencyClassName={style.CharterPriceCurrency}
                                priceClassName={style.CharterPriceAmount}
                                amexClassName={style.AmexNote}
                            />
                        }
                        <QuoteButtons
                            className={classNames(style.ButtonGroup, !showAcceptButton && style.ButtonsRight)}
                            quote={quote}
                            jetCardHolder={jetCardHolder}
                            showAcceptButton={showAcceptButton}
                            disabled={disabled}
                        />
                    </div>
                    {fullscreenImages.length > 0
                        ? <QuoteImageGalleryFullscreen
                            title={imageGalleryTitleText}
                            showCloseButton={true}
                            onClose={this.hideFullscreen}
                            bookingImages={fullscreenImages}
                        />
                        : null
                    }
                    {quote.status === 'Accepted' ?
                        <div className={style.ConfirmationMessage}>
                            <FormattedMessage
                                id={'components.quotes.quote.confirmationMessage'}
                                defaultMessage={
                                    'Your Account Manager is now confirming final availability of the aircraft ' +
                                    'and will contact you shortly.'}
                            />
                        </div>
                        : null
                    }
                </Row>
            </div>
        );
    }
}

function mapStateToProps(state: AppState, ownProps: {}): QuoteElementStateProps {
    return {
        userIsLoggedIn: userIsLoggedIn(state.auth),
    };
}

export const QuoteElement = connect(mapStateToProps)(injectIntl(QuoteElementPresentational));
